/**
 * ReviewStatusFilterBar — the per-contact filter bar for Import Detail's
 * `ContactsTable`, rebuilt on the fu5 two-axis model (epic 2026-09-10,
 * sub-task 01).
 *
 * THE SHAPE, AND WHY IT IS NOT SEVEN CHIPS ANY MORE
 *
 * Its predecessor offered one single-select bar over the seven `review_status`
 * values. That bar could not express the question Jared asked for on
 * 2026-09-08 — *"you can click 'in Thrive, not added to search'"* — because
 * "in Thrive" and "not added to the search" are two different columns'
 * answers, and a single-select control makes you pick one.
 *
 * So the bar now asks three questions, and they compose:
 *
 *   Is in Thrive?      Any / Yes / No / Not checked yet
 *   Added to search?   Any / Yes / No — not yet / No — needs review / No — failed
 *   Skipped?           Any / Yes / No
 *
 * The options ANSWER the question in their label, which is what makes three of
 * the second axis's four values readable as "a NO with its cause" rather than
 * as unrelated states.
 *
 * WHY SELECTS AND NOT MORE CHIPS
 *
 * Three axes as chips is fourteen controls on one line, and the three that
 * used to be chips (`Added to search`, `Skipped`, `Failed`) duplicated the
 * selects exactly (operator, 2026-09-10). The counts survive the move: each
 * option carries its own `(N)`, so nothing that was visible on the old bar is
 * lost — it is one click away instead of zero, on controls that are almost
 * always left at `Any`.
 *
 * THE `All` PILL IS A SEMAPHORE AND A RESET
 *
 * Active only when every axis is at `Any` and the shortcut is off — so at a
 * glance you know whether you are looking at the whole list or a slice. Click
 * it and everything resets. It carries the unfiltered total, which is the
 * number that otherwise disappears the moment you filter.
 *
 * THE ⚑ SHORTCUT
 *
 * `Needs review` is a value of the second axis AND a pill, deliberately: it is
 * the one thing a recruiter wants to check without opening a menu ("is there
 * anything waiting for me?"). The pill and the option set the same filter.
 *
 * `Will duplicate` was retired here (operator, 2026-09-10: *"will duplicate es
 * confuso"*). Nothing is lost: `needs_review` has counted the UNION
 * `to_review ∪ duplicate_linkedin` since fu4, so those rows are already in the
 * shortcut's count. The wording of what to DO about them is a separate task.
 */
import type { ImportContactRow, ReviewStatusCounts } from "@nu-assist/contracts";

/** The seven server-derived statuses (mirrors `ImportContactRow.review_status`). */
export type ReviewStatus = ImportContactRow["review_status"];

/** Retained so callers that still hold a `review_status` selection compile.
 *  The bar no longer RENDERS it — the two axes replaced it — but the `All`
 *  pill resets it, so a value set elsewhere cannot survive invisibly. */
export type ReviewStatusFilter = "all" | NonNullable<ReviewStatus>;

/** "Is in Thrive?" — `"any"` is the inactive value. A `<select>` needs a real
 *  member for "not filtering"; `null` as a DOM value is a footgun. */
export type ThriveIdFilter = "any" | "found" | "not_found" | "not_run";

/** "Added to search?" — three of the four are a NO with its cause. */
export type AddToSearchFilter = "any" | "added" | "not_added" | "needs_review" | "failed";

/** The definition of "this contact is in Thrive" for the Thrive ID cell — we
 *  hold a Thrive id for them, whatever else is true. Unchanged from the
 *  retired bar (Trello #711 cl.4 items 11 + 13): the cell renders the id and
 *  builds the deep link, which is why this stays a type predicate. */
export function isInThrive<T extends Pick<ImportContactRow, "contact_id_thrive">>(
  row: T
): row is T & { contact_id_thrive: string } {
  return row.contact_id_thrive != null;
}

/** The seven-way partition, for the `All` pill's total.
 *
 * Every contact classifies into EXACTLY ONE of these (the property
 * `test_review_status_twins.py` gates), so summing them is the whole-import
 * count under the other active filters. `not_compared` is included even though
 * it has no control of its own — a row in that state is still a row, and
 * leaving it out would make `All` smaller than the list it labels. */
const PARTITION_KEYS = [
  "not_compared",
  "to_review",
  "already_in_thrive",
  "not_in_thrive",
  "exported",
  "ignored",
  "failed",
] as const satisfies readonly (keyof ReviewStatusCounts)[];

function total(counts?: ReviewStatusCounts): number {
  if (!counts) return 0;
  return PARTITION_KEYS.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
}

/** `(N)` suffix, or nothing while the first page is still loading — a `(0)`
 *  shown before the counts arrive would read as "there are none". */
function withCount(label: string, n: number | undefined): string {
  return n === undefined ? label : `${label} (${n})`;
}

/** The "Any ▾" select's look, lit while it filters. Exported for Company
 *  Lists' filter bar (Trello #831), so the two pages' selects cannot drift. */
export const selectClass = (active: boolean) =>
  active
    ? "rounded-md border border-accent bg-accent-muted px-2 py-1 text-label text-accent"
    : "rounded-md border border-border bg-surface px-2 py-1 text-label text-text hover:border-border-hover";

/** The `All` pill's look: lit when nothing is filtered. Shared the same way. */
export const allPillClass = (active: boolean) =>
  active
    ? "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent"
    : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover";

export function ReviewStatusFilterBar({
  value,
  onChange,
  dupLinkedin,
  onDupLinkedinChange,
  counts,
  thriveId = "any",
  onThriveIdChange,
  addToSearch = "any",
  onAddToSearchChange,
  skipped = null,
  onSkippedChange,
}: {
  /** Legacy `review_status` selection. Not rendered; reset by `All`. */
  value: ReviewStatusFilter;
  onChange: (value: ReviewStatusFilter) => void;
  /** Legacy duplicate-LinkedIn toggle. Not rendered; reset by `All`. Its rows
   *  are already inside the `Needs review` count. */
  dupLinkedin: boolean;
  onDupLinkedinChange: (value: boolean) => void;
  /** `counts.review` from the contacts response. Absent while loading. */
  counts?: ReviewStatusCounts;
  thriveId?: ThriveIdFilter;
  onThriveIdChange?: (value: ThriveIdFilter) => void;
  addToSearch?: AddToSearchFilter;
  onAddToSearchChange?: (value: AddToSearchFilter) => void;
  /** `null` = both; `true`/`false` narrow. Tri-state because "both" is the
   *  default and "the ones I have NOT skipped" is the more common narrowing —
   *  a checkbox could only express one of them. */
  skipped?: boolean | null;
  onSkippedChange?: (value: boolean | null) => void;
}) {
  const needsReview = addToSearch === "needs_review";
  const nothingFiltered =
    value === "all" &&
    !dupLinkedin &&
    thriveId === "any" &&
    addToSearch === "any" &&
    skipped === null;

  const resetAll = () => {
    // A no-op when nothing is filtered, rather than a disabled button: a
    // control that appears and disappears is indistinguishable from a bug
    // (operator, 2026-09-10), and the same objection applies to one that
    // greys out.
    if (nothingFiltered) return;
    onChange("all");
    onDupLinkedinChange(false);
    onThriveIdChange?.("any");
    onAddToSearchChange?.("any");
    onSkippedChange?.(null);
  };

  return (
    <fieldset
      className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0"
      data-testid="export-filter"
    >
      <legend className="sr-only">Filter contacts</legend>

      <button
        type="button"
        aria-pressed={nothingFiltered}
        data-chip="all"
        data-testid="review-status-chip-all"
        onClick={resetAll}
        className={allPillClass(nothingFiltered)}
      >
        {withCount("All", counts ? total(counts) : undefined)}
      </button>

      {onThriveIdChange && (
        <label className="flex items-center gap-1.5 text-label text-text-secondary">
          Is in Thrive?
          <select
            data-testid="thrive-id-filter"
            value={thriveId}
            onChange={(event) => onThriveIdChange(event.target.value as ThriveIdFilter)}
            className={selectClass(thriveId !== "any")}
          >
            <option value="any">{withCount("Any", counts ? total(counts) : undefined)}</option>
            <option value="found">{withCount("Yes", counts?.thrive_found)}</option>
            <option value="not_found">{withCount("No", counts?.thrive_not_found)}</option>
            {/* "Not checked yet", not "Not compared yet": under the question
                "is it in Thrive?" the honest answer is that we never looked,
                which is more direct than naming the operation. */}
            <option value="not_run">{withCount("Not checked yet", counts?.thrive_not_run)}</option>
          </select>
        </label>
      )}

      {onAddToSearchChange && (
        <label className="flex items-center gap-1.5 text-label text-text-secondary">
          Added to search?
          <select
            data-testid="add-to-search-filter"
            value={addToSearch}
            onChange={(event) => onAddToSearchChange(event.target.value as AddToSearchFilter)}
            className={selectClass(addToSearch !== "any")}
          >
            <option value="any">{withCount("Any", counts ? total(counts) : undefined)}</option>
            <option value="added">{withCount("Yes", counts?.add_added)}</option>
            {/* The three NOs carry their cause. "No" alone would make the
                recruiter open the row to find out which of three situations
                they are in. */}
            <option value="not_added">{withCount("No, not yet", counts?.add_not_added)}</option>
            <option value="needs_review">
              {withCount("No, needs review", counts?.add_needs_review)}
            </option>
            <option value="failed">{withCount("No, failed", counts?.add_failed)}</option>
          </select>
        </label>
      )}

      {onSkippedChange && (
        <label className="flex items-center gap-1.5 text-label text-text-secondary">
          Skipped?
          <select
            data-testid="skipped-filter"
            value={skipped === null || skipped === undefined ? "any" : skipped ? "yes" : "no"}
            onChange={(event) => {
              const next = event.target.value;
              onSkippedChange(next === "any" ? null : next === "yes");
            }}
            className={selectClass(skipped !== null && skipped !== undefined)}
          >
            <option value="any">{withCount("Any", counts ? total(counts) : undefined)}</option>
            <option value="yes">{withCount("Yes", counts?.mark_skipped)}</option>
            <option value="no">
              {withCount("No", counts ? total(counts) - (counts.mark_skipped ?? 0) : undefined)}
            </option>
          </select>
        </label>
      )}

      {/* The shortcut. Amber, not accent — it flags rows waiting on a human,
          and reusing the accent would make it read as a fourth axis. It sets
          the SAME filter as the option above; the pill exists so the check
          costs no clicks. */}
      {onAddToSearchChange && (
        <button
          type="button"
          aria-pressed={needsReview}
          data-chip="needs_review"
          data-testid="needs-review-chip"
          onClick={() => onAddToSearchChange(needsReview ? "any" : "needs_review")}
          className={
            // The exact classes the retired `Will duplicate` chip carried.
            // `border-warning-strong` and `text-warning-ink`, NOT `border-warning`
            // / `text-warning`: the ink token is the one that clears contrast on
            // the tint, and fu4 already paid for learning that.
            needsReview
              ? "rounded-full border border-warning-strong bg-warning-muted px-3 py-1 text-label text-warning-ink"
              : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover"
          }
        >
          <span aria-hidden="true">⚑ </span>
          {withCount("Needs review", counts?.add_needs_review)}
        </button>
      )}
    </fieldset>
  );
}
